import { forwardRef, type ElementType, type ReactNode, type Ref, type HTMLAttributes } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { resolveSpacing } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps, resolveAs } from '../_internal/factory.js';
import './Box.css';

export interface BoxProps extends BaseProps, Omit<HTMLAttributes<HTMLElement>, 'color'> {
  as?: ElementType;
  children?: ReactNode;
  /** Quick padding (any of the semantic spacing tiers or a number). */
  p?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  px?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  py?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  pt?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  pb?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  pl?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  pr?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  m?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  mx?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  my?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  mt?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  mb?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  ml?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  mr?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  w?: number | string;
  h?: number | string;
  bg?: string;
}

/**
 * `<Box>`: the polymorphic root every primitive composes from. Renders a
 * `<div>` by default; override with `as={Component}` or `as="section"`.
 */
export const Box = forwardRef(function Box(props: BoxProps, ref: Ref<HTMLElement>): ReactNode {
  const { as, children, p, px, py, pt, pb, pl, pr, m, mx, my, mt, mb, ml, mr, w, h, bg, ...rest } = props;
  const Tag = resolveAs(as, 'div');
  const base = useBaseAttrs('rr-box', rest);
  const { dom } = splitBaseProps(rest);

  const inline: Record<string, string | number | undefined> = {
    padding: resolveSpacing(p),
    paddingInline: resolveSpacing(px),
    paddingBlock: resolveSpacing(py),
    paddingTop: resolveSpacing(pt),
    paddingBottom: resolveSpacing(pb),
    paddingLeft: resolveSpacing(pl),
    paddingRight: resolveSpacing(pr),
    margin: resolveSpacing(m),
    marginInline: resolveSpacing(mx),
    marginTop: resolveSpacing(mt),
    marginBottom: resolveSpacing(mb),
    marginLeft: resolveSpacing(ml),
    marginRight: resolveSpacing(mr),
    marginBlock: resolveSpacing(my),
    width: typeof w === 'number' ? `${w}px` : w,
    height: typeof h === 'number' ? `${h}px` : h,
    background: bg
  };
  for (const k of Object.keys(inline)) if (inline[k] === undefined) delete inline[k];

  return (
    <Tag {...dom} ref={ref} {...base.attrs} className={base.className} style={{ ...base.style, ...inline }}>
      {children}
    </Tag>
  );
});
